import {
  Box,
  Button,
  ButtonProps,
  Card,
  DropdownButton,
  DropdownButtonProps,
  Flex,
  InfoIconWithTooltip,
  Title,
} from "@shared/primitive-components"
import { PropsWithChildren, ReactNode, Ref } from "react"
import { ScreenContent, ScreenContentProps } from "../ScreenContent"
import { ListWrapperChildren } from "./ListWrapperChildren"
import * as styles from "./listWrapperStyles"
import { APISearchProps } from "./search/APISearch"
import Search, { SearchProps } from "./search/ListSearch"

export type ListWrapperDataTestIds = {
  root?: string
  buttonLeft?: string
  button?: string
  loading?: string
  search?: {
    field: string
    button: string
  }
  subTitle?: string
  text?: string
  title?: string
}
export type ListWrapperProps<F> = {
  aboveTable?: ReactNode
  apiSearch?: APISearchProps
  belowTable?: ReactNode
  checkboxFilter?: ReactNode
  dataCount?: number
  dataTestIds?: ListWrapperDataTestIds
  downloadFile?: {
    actionTitle: string
    callback?: () => void
  }
  filter?: Omit<DropdownButtonProps<F>, "variant">
  filterLabel?: string
  search?: SearchProps
  subTitle: string
  subTitleTooltip?: string
  button?: ButtonProps & {
    action: () => void
    text: string
  }
  buttonLeft?: ButtonProps & {
    action: () => void
    text: string
  }
  listApiStatus?: {
    failed?: boolean
    loading: boolean
  }
  pageApiStatus?: {
    failed?: boolean
    loading: boolean
  }
  totalLabels?: {
    singular: string
    plural: string
  }
  tableRef?: Ref<HTMLDivElement>
} & Omit<ScreenContentProps, "dataTestIds" | "loading" | "apiFailed">

export const ListWrapper = <F extends unknown>({
  aboveTable = null,
  apiSearch,
  belowTable = null,
  button,
  buttonLeft,
  checkboxFilter,
  children,
  dataCount = 0,
  dataTestIds,
  downloadFile,
  filter,
  filterLabel,
  listApiStatus,
  pageApiStatus,
  search,
  subTitle,
  subTitleTooltip,
  totalLabels,
  tableRef,
  ...screenContentProps
}: PropsWithChildren<ListWrapperProps<F>>) => {
  const showTotalAbove = !!totalLabels && !listApiStatus?.loading
  return (
    <ScreenContent
      {...{
        ...screenContentProps,
        dataTestIds: {
          root: dataTestIds?.root,
          title: dataTestIds?.title,
        },
        apiFailed: pageApiStatus?.failed,
        loading: pageApiStatus?.loading,
      }}
    >
      {aboveTable}
      <Card css={styles.contentWrap} ref={tableRef}>
        <Flex justifyContent="space-between" css={styles.listControlsContainer}>
          <Flex alignItems="center">
            <Title tag="h2" css={styles.title(!!subTitleTooltip)} data-testid={dataTestIds?.subTitle}>
              {subTitle} {showTotalAbove ? `(${dataCount})` : null}
            </Title>
            {subTitleTooltip && <InfoIconWithTooltip content={<Box css={styles.toolTipWrap}>{subTitleTooltip}</Box>} />}
            {button && (
              <Button
                variant={button.variant}
                data-testid={dataTestIds?.button}
                onClick={button.action}
                css={styles.button}
                disabled={button.disabled || listApiStatus?.loading || listApiStatus?.failed}
              >
                {button.text}
              </Button>
            )}
            {buttonLeft && (
              <Button
                variant={buttonLeft.variant}
                data-testid={dataTestIds?.buttonLeft}
                onClick={buttonLeft.action}
                css={styles.buttonLeft}
                disabled={buttonLeft.disabled || listApiStatus?.loading || listApiStatus?.failed}
              >
                {buttonLeft.text}
              </Button>
            )}
            {(search || apiSearch) && (
              <Box
                css={
                  search
                    ? () => styles.searchWrap(!!buttonLeft || !!button)
                    : () => styles.apiSearchWrap(!!buttonLeft || !!button)
                }
              >
                <Search
                  dataTestIds={dataTestIds?.search}
                  apiSearch={apiSearch}
                  search={search}
                  disabled={listApiStatus?.failed || ((!search || search?.value?.length === 0) && dataCount === 0)}
                />
              </Box>
            )}
          </Flex>
          <Flex>
            {(filter || showTotalAbove) && (
              <Flex flexDirection="row" alignItems="center" justifyContent="flex-end">
                {filter && (
                  <>
                    <Box>
                      <DropdownButton
                        {...{
                          ...filter,
                          label: filterLabel,
                          variant: `filter`,
                          disabled: filter.disabled || listApiStatus?.loading || listApiStatus?.failed,
                        }}
                      />
                    </Box>
                  </>
                )}
              </Flex>
            )}
            {downloadFile && (
              <Button
                data-testid="download-button"
                disabled={!downloadFile.callback}
                onClick={downloadFile.callback}
                css={styles.buttonLeft}
              >
                {downloadFile.actionTitle}
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex
          justifyContent={filter || showTotalAbove ? `space-between` : `flex-end`}
          alignItems="center"
          css={styles.actionWrap}
        />
        <Box width="100%">
          {checkboxFilter}
          <ListWrapperChildren
            apiFailed={listApiStatus?.failed}
            loading={listApiStatus?.loading}
            dataTestIds={{ loading: dataTestIds?.loading, text: dataTestIds?.text }}
          >
            {children}
          </ListWrapperChildren>
        </Box>
      </Card>
      {belowTable}
    </ScreenContent>
  )
}

ListWrapper.defaultProps = {
  aboveTable: null,
  listApiStatus: {
    failed: false,
    loading: false,
  },
  pageApiStatus: {
    failed: false,
    loading: false,
  },
  filter: null,
  filterLabel: null,
  totalLabels: null,
  subTitleTooltip: undefined,
  apiSearch: undefined,
  belowTable: null,
  downloadFile: undefined,
  buttonLeft: undefined,
  button: undefined,
  dataCount: 0,
  dataTestIds: undefined,
  search: undefined,
  checkboxFilter: null,
  tableRef: null,
}
